/**
 * Relay-owned Kalshi ticker → market classifier and Hydra sport normalizer.
 *
 * Why this lives in the relay and not as a shared import from the frontend
 * parser: the relay is a separate tsconfig project (rootDir: "src") so a
 * cross-package compile-time import would require either a real workspace
 * package or TS project references — neither is in scope for trader rules.
 * We duplicate the small surface we need (prefix → sport) here and rely on
 * the fixture tests below + the fail-closed LIMIT_MARKET_SPORT_UNKNOWN
 * code as the runtime safety net.
 *
 * If you add a sport prefix to src/lib/kalshiTicker/sports/<sport>.ts on
 * the frontend, also add a SUPPORTED_SPORT_PREFIXES entry here AND a
 * test fixture in sports.test.ts. The relay/frontend taxonomy drift is
 * caught at runtime by failing closed (any unmatched prefix becomes
 * { kind: 'unknown' }, which under any active rule rejects with
 * LIMIT_MARKET_SPORT_UNKNOWN) rather than at compile time.
 */

export type Sport =
  | 'MLB'
  | 'NBA'
  | 'WNBA'
  | 'NFL'
  | 'NHL'
  | 'NCAA'
  | 'Tennis'
  | 'Soccer'
  | 'CFB'
  | 'UFC'
  | 'Boxing'
  | 'Cricket'
  | 'Esports'
  | 'Golf';

export const KALSHI_SPORTS: readonly Sport[] = [
  'MLB',
  'NBA',
  'WNBA',
  'NFL',
  'NHL',
  'NCAA',
  'Tennis',
  'Soccer',
  'CFB',
  'UFC',
  'Boxing',
  'Cricket',
  'Golf',
  'Esports',
];

export type NonSportCategory = 'politics' | 'crypto' | 'other';

export type MarketClassification =
  | { kind: 'sport'; sport: Sport }
  | { kind: 'nonSport'; category: NonSportCategory }
  | { kind: 'unknown' };

// Prefix → sport map. Mirrors the dispatch in src/lib/kalshiTicker/parse.ts.
//
// Policy (see docs/hydra-sports-upgrades.md): classify the broadest *verified*
// stable sport-family root rather than enumerating bet types. A single broad
// root (e.g. KXMLB) covers every current and future bet family under it
// (KXMLBGAME, KXMLBF5, KXMLBTEAMTOTAL, KXMLBRFI, …), so a new Kalshi bet type
// under a covered root needs NO relay change — it classifies correctly the
// moment it appears. This replaces the previous game/spread/total/series
// enumeration, which silently dropped live prop/outright families to
// { kind: 'unknown' } and rejected legitimate orders with
// LIMIT_MARKET_SPORT_UNKNOWN (verified against the 2026-07-30 Hydra snapshot:
// 24 unknown roots / 2,897 live markets, all under these five roots).
//
// Order still matters: a broad root is only safe when no OTHER sport's ticker
// is a superstring of it (matching is first-hit-wins via startsWith). None of
// the five broad roots below has a cross-sport superstring in the live audit.
// The WNBA-before-NBA block below is a deliberate belt-and-suspenders discipline
// (KXWNBA is not actually a superstring of KXNBA, so it can't be swallowed — but
// keep the ordering so a future bare-NBA edit can't regress it), and KXNCAAF
// (CFB) is kept distinct from the KXNCAA* basketball roots.
const KALSHI_SPORT_PREFIXES: ReadonlyArray<[string, Sport]> = [
  // WNBA — listed before NBA so KXWNBA never falls into the KXNBA bucket.
  ['KXWNBASERIES', 'WNBA'],
  ['KXWNBASPREAD', 'WNBA'],
  ['KXWNBATOTAL', 'WNBA'],
  ['KXWNBAGAME', 'WNBA'],
  ['KXWNBA', 'WNBA'],
  // NBA
  ['KXNBASERIES', 'NBA'],
  ['KXNBASPREAD', 'NBA'],
  ['KXNBATOTAL', 'NBA'],
  ['KXNBAGAME', 'NBA'],
  ['KXNBA', 'NBA'],
  // NFL — broad root covers game/spread/total, TD props, and AFC/NFC
  // championship outrights (KXNFLAFCCHAMP, KXNFLNFCCHAMP). KXSB (Super Bowl)
  // is a genuinely separate root, not under KXNFL, so it keeps its own entry.
  ['KXNFL', 'NFL'],
  ['KXSB', 'NFL'],
  // NHL — broad root covers game/spread/total/series plus league (KXNHL) and
  // conference (KXNHLEAST, KXNHLWEST) outrights.
  ['KXNHL', 'NHL'],
  // MLB — broad root covers game/spread/total/series plus every live prop and
  // outright family: F5 (KXMLBF5/SPREAD/TOTAL), team total, first-inning run
  // (RFI), extras, pitcher K's/outs (KS/OUTS), hits, HR, RBI, stolen bases,
  // total bases, and league/pennant roots (KXMLBAL/KXMLBNL). Every KXMLB* root
  // normalized to MLB in the live audit; no cross-sport superstring exists.
  ['KXMLB', 'MLB'],
  // NCAA basketball — men's/women's/generic all collapse to NCAA. Men's by its family root (2026-11-03, the season
  // on: 1H / 2H lines and props such as KXNCAAMBFIRST3PT / KXNCAAMBRACE fell through as unknown under the three exact
  // families). No other sport's root is a superstring of KXNCAAMB: KXNCAAF (college football, below) shares only KXNCAA.
  ['KXNCAAMB', 'NCAA'],
  ['KXNCAAWBSPREAD', 'NCAA'],
  ['KXNCAAWBTOTAL', 'NCAA'],
  ['KXNCAAWBGAME', 'NCAA'],
  ['KXNCAAGAME', 'NCAA'],
  // Tennis — broad ATP + WTA roots (tour outrights + their MATCH families)
  // plus ITF/125K-style lower-tier series all collapse to Tennis. ITF is a
  // separate root, not under KXATP/KXWTA, so it keeps its own entries.
  ['KXATP', 'Tennis'],
  ['KXWTA', 'Tennis'],
  ['KXITFWMATCH', 'Tennis'],
  ['KXITFMATCH', 'Tennis'],
  // ITF doubles (2026-10-28): classified so the blotter and the sport filters name them; quoting is unchanged.
  ['KXITFWDOUBLES', 'Tennis'],
  ['KXITFDOUBLES', 'Tennis'],
  // Soccer — classify the league family, not an enumerated list of bet types.
  // The frontend parser supports GAME/SPREAD/TOTAL plus knockout ADVANCE,
  // 1H/2H and BTTS variants. Enumerating only the first three caused supported
  // soccer markets such as KXWCADVANCE to fall through as unknown; managers
  // then had to enable non-sports just to let a soccer order through. Family
  // prefixes keep new market variants in the sport rule without weakening the
  // fail-closed behavior for genuinely unknown series.
  ['KXINTLFRIENDLY', 'Soccer'],
  ['KXUECL', 'Soccer'],
  ['KXUEL', 'Soccer'],
  ['KXUCL', 'Soccer'],
  ['KXMLS', 'Soccer'],
  ['KXEPL', 'Soccer'],
  ['KXLALIGA', 'Soccer'],
  ['KXBUNDESLIGA', 'Soccer'],
  ['KXWC', 'Soccer'],
  // Club leagues (2026-09-09, Serie A / Bundesliga / La Liga feed window):
  // every root verified against the live Kalshi catalog — all descendants
  // are Soccer-tagged (KXEPL* 38, KXMLS* 33, KXUCL* 55, KXUEL* 25, KXSERIEA*
  // 31, KXBUNDESLIGA* 35, KXLALIGA* 36), no other sport's ticker is a
  // superstring (the basketball leagues are KXBBSERIEA / KXBBL). Before this,
  // EPL/MLS/UCL/UEL orders classified `unknown` for restricted traders.
  // NOTE: KXUSOPEN is soccer's US Open Cup AND one golf series — not a root.
  ['KXEPL', 'Soccer'],
  ['KXMLS', 'Soccer'],
  ['KXUCL', 'Soccer'],
  ['KXUEL', 'Soccer'],
  ['KXSERIEA', 'Soccer'],
  ['KXBUNDESLIGA', 'Soccer'],
  ['KXLALIGA', 'Soccer'],
  // Americas + Nations Leagues (2026-09-25 choke audit: ~12% of skips were these leagues' GAME tickers read as
  // "multi_outcome" because no rule said they were soccer). Prefixes taken from the live RFQ tape; none is a
  // prefix of another sport's series.
  ['KXLIGAMX', 'Soccer'], // Liga MX
  ['KXLIGAEXP', 'Soccer'], // Liga de Expansión MX
  ['KXCONCACAFNL', 'Soccer'], // CONCACAF Nations League
  ['KXUEFANL', 'Soccer'], // UEFA Nations League
  ['KXDIMAYOR', 'Soccer'], // Colombia Primera A (Dimayor)
  // Every other soccer league / cup on Kalshi (2026-10-17 top-skips audit: 99 % of `multi_outcome` skips were these
  // leagues' match-winner tickers — Ligue 1 alone in 78 % — read as 3-way because no rule said they were soccer).
  // Generated from Kalshi's live series catalog: each root's every descendant across ALL 15,187 series is
  // Soccer-tagged, and no root is a prefix of (or prefixed by) any root above. Roots whose family also holds a
  // non-soccer series are listed by series instead: USL (KXUSLEI), UEFA (KXUEFAMENTION…), FIFA (KXFIFATRAVEL…);
  // KXEFLTROPHY is left out (its TOTAL series is untagged). Longest first.
  ['KXDANISHSUPERLIGA', 'Soccer'],
  ['KXEFLCHAMPIONSHIP', 'Soccer'],
  ['KXCOUPEDEFRANCE', 'Soccer'],
  ['KXBALLERLEAGUE', 'Soccer'],
  ['KXCONCACAFCCUP', 'Soccer'],
  ['KXCOPADOBRASIL', 'Soccer'],
  ['KXDENSUPERLIGA', 'Soccer'],
  ['KXLIGAPORTUGAL', 'Soccer'],
  ['KXSCOTTISHPREM', 'Soccer'],
  ['KXUSLTEAMTOTAL', 'Soccer'],
  ['KXALLSVENSKAN', 'Soccer'],
  ['KXCONMEBOLLIB', 'Soccer'],
  ['KXCONMEBOLSUD', 'Soccer'],
  ['KXCOPPAITALIA', 'Soccer'],
  ['KXEKSTRAKLASA', 'Soccer'],
  ['KXELITESERIEN', 'Soccer'],
  ['KXESPSUPERCUP', 'Soccer'],
  ['KXFINALISSIMA', 'Soccer'],
  ['KXFRASUPERCUP', 'Soccer'],
  ['KXINTERCONCUP', 'Soccer'],
  ['KXITASUPERCUP', 'Soccer'],
  ['KXNCAAMSOCCER', 'Soccer'],
  ['KXSWISSLEAGUE', 'Soccer'],
  ['KXARGPREMDIV', 'Soccer'],
  ['KXBRASILEIRO', 'Soccer'],
  ['KXCOPADELREY', 'Soccer'],
  ['KXEREDIVISIE', 'Soccer'],
  ['KXFIFAUSPULL', 'Soccer'],
  ['KXLEAGUESCUP', 'Soccer'],
  ['KXUSLADVANCE', 'Soccer'],
  ['KXAUTBUNDES', 'Soccer'],
  ['KXBELGIANPL', 'Soccer'],
  ['KXEERSTEDIV', 'Soccer'],
  ['KXISRSUPCUP', 'Soccer'],
  ['KXTWEEDEDIV', 'Soccer'],
  ['KXUSLSPREAD', 'Soccer'],
  ['KXUSOPENCUP', 'Soccer'],
  ['KXDFBPOKAL', 'Soccer'],
  ['KXJ2LEAGUE', 'Soccer'],
  ['KXK2LEAGUE', 'Soccer'],
  ['KXPERLIGA1', 'Soccer'],
  ['KXPRYLIGA1', 'Soccer'],
  ['KXSLGREECE', 'Soccer'],
  ['KXSUPERLIG', 'Soccer'],
  ['KXTACAPORT', 'Soccer'],
  ['KXUSLTOTAL', 'Soccer'],
  ['KXVENFUTVE', 'Soccer'],
  ['KXVLEAGUE1', 'Soccer'],
  ['KXALEAGUE', 'Soccer'],
  ['KXARGNACB', 'Soccer'],
  ['KXBOLPDIV', 'Soccer'],
  ['KXISRSCUP', 'Soccer'],
  ['KXJLEAGUE', 'Soccer'],
  ['KXKLEAGUE', 'Soccer'],
  ['KXKNVBCUP', 'Soccer'],
  ['KXSAUDIPL', 'Soccer'],
  ['KXTFF1LIG', 'Soccer'],
  ['KXUSLBTTS', 'Soccer'],
  ['KXUSLGAME', 'Soccer'],
  ['KXAPFDDH', 'Soccer'],
  ['KXCHLLDP', 'Soccer'],
  ['KXCLUBWC', 'Soccer'],
  ['KXCZEFNL', 'Soccer'],
  ['KXEFLCUP', 'Soccer'],
  ['KXFINCUP', 'Soccer'],
  ['KXFRAPLW', 'Soccer'],
  ['KXFROCUP', 'Soccer'],
  ['KXGRECUP', 'Soccer'],
  ['KXHUNNB1', 'Soccer'],
  ['KXLIGUE1', 'Soccer'],
  ['KXLIGUE2', 'Soccer'],
  ['KXLVAVIR', 'Soccer'],
  ['KXQSTARS', 'Soccer'],
  ['KXSCOCUP', 'Soccer'],
  ['KXSERIEB', 'Soccer'],
  ['KXSERIEC', 'Soccer'],
  ['KXSRBCUP', 'Soccer'],
  ['KXSVKCUP', 'Soccer'],
  ['KXSVNCUP', 'Soccer'],
  ['KXTHAIL1', 'Soccer'],
  ['KXUEFASC', 'Soccer'],
  ['KXUSLCUP', 'Soccer'],
  ['KXAFCAC', 'Soccer'],
  ['KXAFCCL', 'Soccer'],
  ['KXAFCON', 'Soccer'],
  ['KXASEAN', 'Soccer'],
  ['KXCANPL', 'Soccer'],
  ['KXCHNL1', 'Soccer'],
  ['KXCHNSL', 'Soccer'],
  ['KXCLUBF', 'Soccer'],
  ['KXCZEFL', 'Soccer'],
  ['KXECULP', 'Soccer'],
  ['KXEFLL1', 'Soccer'],
  ['KXEGYPL', 'Soccer'],
  ['KXENGCS', 'Soccer'],
  ['KXENGNL', 'Soccer'],
  ['KXETTAN', 'Soccer'],
  ['KXFACUP', 'Soccer'],
  ['KXFIFAW', 'Soccer'],
  ['KXFINYL', 'Soccer'],
  ['KXFROPL', 'Soccer'],
  ['KXGER3L', 'Soccer'],
  ['KXGERSC', 'Soccer'],
  ['KXIDNSL', 'Soccer'],
  ['KXINDSL', 'Soccer'],
  ['KXISRNL', 'Soccer'],
  ['KXISRPL', 'Soccer'],
  ['KXLIGAF', 'Soccer'],
  ['KXROML1', 'Soccer'],
  ['KXSAPSL', 'Soccer'],
  ['KXSGPPL', 'Soccer'],
  ['KXSRBSL', 'Soccer'],
  ['KXSVK2L', 'Soccer'],
  ['KXSVNPL', 'Soccer'],
  ['KXUAEPL', 'Soccer'],
  ['KXURYPD', 'Soccer'],
  ['KXUSL1H', 'Soccer'],
  ['KXEWSL', 'Soccer'],
  ['KXMYSL', 'Soccer'],
  ['KXNWSL', 'Soccer'],
  ['KXHNL', 'Soccer'],
  // Golf (2026-09-09): the Presidents Cup (KXPRESCUP, team-event winner) is
  // the first Kalshi golf series on the plane; KXPGA is the PGA family root
  // (68 catalog series, all golf) that the Ryder-Cup-style day/match series
  // will land under. Other tours (KXLIV, KXDPW, KXLPGA, KXKFTOUR, KXMASTERS,
  // KXTHEOPEN, KXTGL, KXCHAMPTOUR) are verified golf-only in the catalog but
  // not yet on the feed — add when they flow (§9: never a speculative root).
  ['KXPRESCUP', 'Golf'],
  ['KXPGA', 'Golf'],
  // CFB — college football. Kalshi files it under KXNCAAF (distinct from the
  // KXNCAA* basketball prefixes above; no overlap — 'KXNCAAFGAME' is not a
  // prefix of, nor prefixed by, any KXNCAA basketball entry).
  // Use the family root so newly listed spread/total and future CFB bet types
  // remain inside the trader's CFB rule instead of falling through as unknown.
  ['KXNCAAF', 'CFB'],
  // UFC
  ['KXUFCFIGHT', 'UFC'],
  // Every UFC family (method of finish KXUFCMOF, method of victory, distance, rounds — comboBlocklist lists them;
  // Stuart, 2026-10-31: MOF legs had no sport, so no game key and no start).
  ['KXUFC', 'UFC'],
  // Boxing
  ['KXBOXING', 'Boxing'],
  // Cricket — T20 internationals.
  ['KXWT20MATCH', 'Cricket'],
  // Golf: verified against the venue's Sports series catalogue (2026-09-08).
  // Use stable tour roots, but NOT KXGOLF (also Golf/Tennis mixed markets) or
  // KXUSOPEN (also US Open Cup soccer). Narrow standalone series include the
  // '-' boundary. In particular Presidents Cup must precede KXPRES politics.
  ['KXPGA', 'Golf'],
  ['KXLPGA', 'Golf'],
  ['KXLIV', 'Golf'],
  ['KXDPWORLDTOUR', 'Golf'],
  ['KXDPWT', 'Golf'],
  ['KXTGL', 'Golf'],
  ['KXCHAMPTOUR', 'Golf'],
  ['KXRYDERCUP', 'Golf'],
  ['KXMASTERS', 'Golf'],
  ['KXGOLFH2H-', 'Golf'],
  ['KXGOLFNEXTWIN-', 'Golf'],
  ['KXGOLFMAJORFUT-', 'Golf'],
  ['KXGOLFMAJOR-', 'Golf'],
  ['KXGOLFMAJORS-', 'Golf'],
  ['KXGOLFTOURN-', 'Golf'],
  ['KXGOLFCAT-', 'Golf'],
  ['KXPRESCUP-', 'Golf'],
  ['KXTHEOPEN-', 'Golf'],
  ['KXUSOPEN-', 'Golf'],
  ['KXBRYSONCOURSERECORDS-', 'Golf'],
  ['KXNEXTWIN-', 'Golf'],
  ['KXTIGERMASTERS-', 'Golf'],
  ['KXKFTOUR-', 'Golf'],
  ['KXNELKHOLEINONE-', 'Golf'],
  ['KXPHOENIXOPEN-', 'Golf'],
  ['KXSCOTTIESLAM-', 'Golf'],
  ['KXNELKRTS-', 'Golf'],
  ['KXOWGRRANK-', 'Golf'],
  ['KXGENESISINVITATIONAL-', 'Golf'],
  ['KXHOLEINONE-', 'Golf'],
  ['KXBDSHOLEINONE-', 'Golf'],
  // Esports — CS2 / Dota 2 / Valorant / LoL all collapse to Esports.
  ['KXCS2GAME', 'Esports'],
  ['KXDOTA2GAME', 'Esports'],
  ['KXVALORANTGAME', 'Esports'],
  ['KXLOLGAME', 'Esports'],
];

/** Hydra FLOW LABELS — series partitions core publishes under their own wire
 *  sport code so a different edge can serve each slice (NFLPROPS / NFLTOTALS
 *  are NFL; CFBPROPS is CFB). This is a ROUTING vocabulary for the fleet
 *  (which engine owns a ticker's books), NOT a classification: the trader
 *  permission gate above still maps by broad root (KXNFL → NFL), and
 *  normalizeHydraSport folds each label back onto its base sport. Keys are
 *  the exact Kalshi series token (`ticker.split('-')[0]`). */
export const FLOW_LABEL_SERIES: Record<string, readonly string[]> = {
  NFLPROPS: [
    'KXNFLANYTD', 'KXNFLPASSYDS', 'KXNFLPASSTDS', 'KXNFLPASSATT', 'KXNFLPASSCOMP', 'KXNFLPASSINT',
    'KXNFLRECYDS', 'KXNFLREC', 'KXNFLRSHYDS', 'KXNFLRSHATT', 'KXNFLRRYDS', 'KXNFLLONGREC',
    'KXNFLLONGRSH', 'KXNFLTKL', 'KXNFLSACK', 'KXNFLINT', 'KXNFLDSTTD', 'KXNFLFIRSTTD', 'KXNFL2TD',
    'KXNFLTD', 'KXNFLRACE', 'KXNFLBOTH', 'KXNFLTOTALTD', 'KXNFLTEAMFIRSTTD', 'KXNFL2PTCONV',
  ],
  NFLTOTALS: [
    'KXNFLTOTAL', 'KXNFL1H', 'KXNFL1HSPREAD', 'KXNFL1HTOTAL', 'KXNFL1HFT', 'KXNFL1HTEAMTOTAL',
    'KXNFL2H', 'KXNFL2HSPREAD', 'KXNFL2HTOTAL', 'KXNFL1Q', 'KXNFL1QSPREAD', 'KXNFL1QTOTAL',
    'KXNFL1QBTTS', 'KXNFL2Q', 'KXNFL2QSPREAD', 'KXNFL2QTOTAL', 'KXNFL2QBTTS', 'KXNFL3Q',
    'KXNFL3QSPREAD', 'KXNFL3QTOTAL', 'KXNFL3QBTTS', 'KXNFL4Q', 'KXNFL4QSPREAD', 'KXNFL4QTOTAL',
    'KXNFL4QBTTS', 'KXNFLOT', 'KXNFLTEAMTOTAL', 'KXNFLEQBTTS',
  ],
  CFBPROPS: [
    'KXNCAAFTEAMTOTAL', 'KXNCAAFTEAMYDS', 'KXNCAAFTEAMTD', 'KXNCAAFTEAMFG', 'KXNCAAFTEAMINT',
    'KXNCAAFTEAMSACK', 'KXNCAAFTEAMTO', 'KXNCAAFTEAMREC', 'KXNCAAFTEAMRECTD', 'KXNCAAFTEAMRECYDS',
    'KXNCAAFTEAMRSHATT', 'KXNCAAFTEAMRSHTD', 'KXNCAAFTEAMRSHYDS', 'KXNCAAFDSTTD', 'KXNCAAFTOTALTD',
    'KXNCAAFTOTALFG', 'KXNCAAFFTTS', 'KXNCAAFFIRSTTDTEAM', 'KXNCAAFQHIGHSCORE', 'KXNCAAF2PT',
    'KXNCAAFEQBTTS', 'KXNCAAFMOSTOT',
  ],
  // Soccer goalscorer props (2026-09-09): per-player goal ladder + first
  // goalscorer for every club league the fleet carries. Mirrors
  // deploy/superhydra/fleet.json flows.SOCCERPROPS.series exactly.
  SOCCERPROPS: [
    'KXEPLGOAL', 'KXUCLGOAL', 'KXMLSGOAL', 'KXWCGOAL', 'KXSERIEAGOAL', 'KXBUNDESLIGAGOAL', 'KXLALIGAGOAL',
    'KXEPLFIRSTGOAL', 'KXUCLFIRSTGOAL', 'KXMLSFIRSTGOAL', 'KXWCFIRSTGOAL', 'KXSERIEAFIRSTGOAL', 'KXBUNDESLIGAFIRSTGOAL', 'KXLALIGAFIRSTGOAL',
  ],
};

const SERIES_TO_FLOW_LABEL: ReadonlyMap<string, string> = new Map(
  Object.entries(FLOW_LABEL_SERIES).flatMap(([label, series]) => series.map((s) => [s, label] as const)),
);

/** The flow label a Kalshi series token belongs to, or null when the series
 *  rides its sport's base label. Exact-token match, never a prefix: KXNFLTOTAL
 *  is NFLTOTALS but KXNFLTOTALTD is NFLPROPS. */
export function flowLabelForSeries(series: string): string | null {
  return SERIES_TO_FLOW_LABEL.get(series.toUpperCase()) ?? null;
}

// Non-sport prefixes we recognize. Anything else is { kind: 'unknown' }.
const KALSHI_NONSPORT_PREFIXES: ReadonlyArray<[string, NonSportCategory]> = [
  ['KXATTY', 'politics'],
  ['KXPRES', 'politics'],
  // Kalshi's legacy election series carry no KX (Stuart, 2026-10-27: CONTROLS-2026-D, SENATEAK-26-D read 'sport
  // unknown'). No sport prefix starts without KX, so these shadow nothing.
  ['CONTROLS', 'politics'],
  ['CONTROLH', 'politics'],
  ['SENATE', 'politics'],
  ['HOUSE', 'politics'],
  ['GOV', 'politics'],
  ['KXBTC', 'crypto'],
  ['KXETH', 'crypto'],
];

/**
 * The longest prefix either list can match, so a classification depends ONLY on a ticker's first
 * `MAX_PREFIX_LEN` characters — the key the memo below is allowed to collapse tickers onto.
 */
const MAX_PREFIX_LEN = Math.max(
  ...KALSHI_SPORT_PREFIXES.map(([p]) => p.length),
  ...KALSHI_NONSPORT_PREFIXES.map(([p]) => p.length),
);
/**
 * Prefix-match memo (2026-10-18). The scan is first-match-wins over ~210 entries and runs several times per LEG on
 * the quote path (multiOutcome, betFamily, fairResolver, the skip labels) — ~25k calls/s at NFL-Sunday rates. The
 * 2026-10-17 soccer-coverage fix grew the sport list from 87 to 208 entries, and the engine's decide p90 doubled
 * (69 → 132 ms) the same day. Keying on `slice(0, MAX_PREFIX_LEN)` is EXACTLY equivalent to scanning the whole
 * ticker — no prefix is longer than that — so this trades one bounded Map for the repeated scan.
 */
const classifyMemo = new Map<string, MarketClassification>();
/** Distinct keys are bounded by Kalshi's series+event prefixes; clear rather than grow without limit. */
const CLASSIFY_MEMO_MAX = 50_000;

function classifyUncached(key: string): MarketClassification {
  for (const [prefix, sport] of KALSHI_SPORT_PREFIXES) {
    if (key.startsWith(prefix)) return Object.freeze({ kind: 'sport', sport });
  }
  for (const [prefix, category] of KALSHI_NONSPORT_PREFIXES) {
    if (key.startsWith(prefix)) return Object.freeze({ kind: 'nonSport', category });
  }
  return UNKNOWN_CLASSIFICATION;
}
const UNKNOWN_CLASSIFICATION: MarketClassification = Object.freeze({ kind: 'unknown' });

/** Classify a Kalshi ticker via prefix-matching. Returns 'unknown' for
 *  anything that doesn't match either the sport list or the non-sport list.
 *  Results are frozen and shared — callers must not mutate them. */
export function classifyKalshiTicker(ticker: string): MarketClassification {
  if (typeof ticker !== 'string' || ticker.length === 0) {
    return UNKNOWN_CLASSIFICATION;
  }
  const key = ticker.length > MAX_PREFIX_LEN ? ticker.slice(0, MAX_PREFIX_LEN) : ticker;
  const hit = classifyMemo.get(key);
  if (hit !== undefined) return hit;
  const out = classifyUncached(key);
  if (classifyMemo.size >= CLASSIFY_MEMO_MAX) classifyMemo.clear();
  classifyMemo.set(key, out);
  return out;
}

/** Test seam: the memo is keyed on ticker prefixes only, so it never needs clearing in production. */
export function _clearClassifyMemoForTest(): void {
  classifyMemo.clear();
}

/** Hydra emits its own sport taxonomy as UPPERCASE codes on the markets frame
 *  (SOCCER, ATP, WTA, CFB, UFC, BOXING, CRICKET, ESPORTS, MLB, CBB, ...).
 *  Normalize into our Sport enum, case-insensitively. Returns null for codes we
 *  don't classify (e.g. F1) so the resolver falls through to
 *  LIMIT_MARKET_SPORT_UNKNOWN for restricted traders.
 *
 *  The case-fold is load-bearing: this is the Polymarket order path
 *  (polyResolver → here). A case-sensitive Title-case switch silently nulled
 *  SOCCER and ATP/WTA, leaving those Poly orders rejected even after the sports
 *  were added to the taxonomy. */
export function normalizeHydraSport(hydraSport: string | null | undefined): Sport | null {
  if (typeof hydraSport !== 'string' || hydraSport.length === 0) return null;
  switch (hydraSport.toUpperCase()) {
    case 'MLB':
      return 'MLB';
    case 'NBA':
      return 'NBA';
    case 'WNBA':
      return 'WNBA';
    case 'NFL':
    // Flow labels (FLOW_LABEL_SERIES) fold back onto their base sport.
    case 'NFLPROPS':
    case 'NFLTOTALS':
      return 'NFL';
    case 'NHL':
      return 'NHL';
    case 'CBB':
      // Hydra's NCAA men's basketball code.
      return 'NCAA';
    case 'ATP':
    case 'WTA':
    case 'TENNIS':
      // Hydra splits tennis into ATP/WTA tours; both collapse to Tennis.
      return 'Tennis';
    case 'SOCCER':
    case 'SOCCERPROPS':
      return 'Soccer';
    case 'CFB':
    case 'CFBPROPS':
      return 'CFB';
    case 'UFC':
      return 'UFC';
    case 'BOXING':
      return 'Boxing';
    case 'CRICKET':
      return 'Cricket';
    case 'GOLF':
      return 'Golf';
    case 'ESPORTS':
      return 'Esports';
    // Hydra-known but still unclassified: F1, ...
    default:
      return null;
  }
}

/** Convenience for callers that don't care about the kind tag. */
/** The ticker roots that classify as `sport` (2026-10-31, the quotes exports' `sport=` filter): the broad roots and
 *  their narrower families, so a SQL prefix match reads the same table the classifier does. */
export function kalshiSportPrefixes(sport: Sport): string[] {
  return KALSHI_SPORT_PREFIXES.filter(([, s]) => s === sport).map(([p]) => p);
}

export function sportFromKalshiTicker(ticker: string): Sport | null {
  const c = classifyKalshiTicker(ticker);
  return c.kind === 'sport' ? c.sport : null;
}

/** Combo / MVE wrappers (`KXMVE*`) have no sport prefix. Classify the
 *  underlying legs. A wrapper with no classifiable legs stays unknown. */
export function classifyKalshiMarket(
  ticker: string,
  legTickers?: readonly string[] | null,
): MarketClassification[] {
  const primary = classifyKalshiTicker(ticker);
  if (primary.kind !== 'unknown') return [primary];
  const legs = [
    ...new Set((legTickers ?? []).map((t) => (typeof t === 'string' ? t.trim() : '')).filter((t) => t.length > 0)),
  ];
  if (legs.length === 0) return [primary];
  return legs.map((t) => classifyKalshiTicker(t));
}
